import React, { FC, MouseEvent, useMemo } from 'react'
import classNames from 'classnames'
import AttachmentIcon from 'transfix-icons/build/AttachmentIcon'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import { Card } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Typography } from 'transfix-ui/core'
import { IFileCreate } from '../types'
import { formatFileSizeInMB } from '../utils/fileUtils'

/**
 * Default function for `getTitle` prop
 */
const getTitleDefault = (attachment: IAttachmentsListItem): string => {
  const { fileFileName = '', filename = '' } = attachment || {}
  return filename || fileFileName || 'Untitled Attachment'
}

export interface IAttachmentsListItem extends IFileCreate {
  id?: number
  url?: string
}

export interface IAttachmentListItemProps {
  className?: string
  attachment: IAttachmentsListItem
  index: number
  /**
   * @default true
   */
  showIcon?: boolean
  /**
   * Add a title for the attachment that is
   * @default {getTitleDefault}
   */
  getTitle?: (file: IAttachmentsListItem) => string
  onFileDelete: (event: MouseEvent<HTMLButtonElement>) => void
  isLoading?: boolean
}

/**
 * Individual row item in <AttachmentList />
 */
export const AttachmentListItem: FC<IAttachmentListItemProps> = ({
  className = '',
  attachment,
  index,
  isLoading,
  getTitle: getTitleProp = getTitleDefault,
  onFileDelete,
  showIcon = true,
}) => {
  const id = attachment?.id || attachment?.uuid
  const title = useMemo(() => getTitleProp(attachment), [attachment, getTitleProp])
  const { size = 0, url } = attachment || {}
  const { label } = formatFileSizeInMB(size)

  return (
    <Card
      dataTest={`attachment-list-link-${index + 1}`}
      className={classNames(className, 'pa2')}
      key={id}>
      <div className='flex flex-row items-center'>
        {showIcon && <AttachmentIcon size='xlarge' color='coolGray5' />}
        {url ? (
          <a className='pl1' href={url} target='_blank' rel='noopener noreferrer'>
            {title}
          </a>
        ) : (
          <div>{title}</div>
        )}
        {size && (
          <Typography variant='caption' className='ml2'>
            {label}
          </Typography>
        )}
        <div className='ml-auto'>
          <IconButton
            id={`${id}`}
            onClick={onFileDelete}
            Icon={DeleteIcon}
            size='large'
            label='Delete'
            disabled={isLoading}
          />
        </div>
      </div>
    </Card>
  )
}
